import type { ImportSource, Team } from '@nexo/types';
import { Button, Progress, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/forms';
import { keys } from '../../lib/queries';

const sources: { id: ImportSource; name: string; accept: string; how: string }[] = [
  {
    id: 'slack',
    name: 'Slack',
    accept: '.zip',
    how: 'Ajustes del espacio de trabajo → Importar/Exportar datos → Exportar. Sube el .zip: se crean los canales públicos con sus mensajes e hilos.',
  },
  {
    id: 'trello',
    name: 'Trello',
    accept: '.json,application/json',
    how: 'Menú del tablero → Imprimir, exportar y compartir → Exportar como JSON. Cada tarjeta se convierte en una tarea.',
  },
  {
    id: 'jira',
    name: 'Jira',
    accept: '.csv,text/csv',
    how: 'Filtros → Exportar → CSV (todos los campos). Cada incidencia se convierte en una tarea con su tipo, prioridad y estado.',
  },
];

/** «Importar desde…»: bring a team's history over from Slack, Trello or Jira. */
export function ImportSection({ team }: { team: Team }) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [source, setSource] = useState<(typeof sources)[number] | null>(null);
  const [progress, setProgress] = useState(0);

  const run = useMutation({
    mutationFn: ({ from, file }: { from: ImportSource; file: File }) =>
      api.teams.import(team.id, from, file, { onProgress: setProgress }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: keys.channels(team.id) });
      void queryClient.invalidateQueries({ queryKey: keys.tasks(team.id) });
      toast({
        title: t('Importación terminada'),
        description:
          result.tasks > 0
            ? t('{{count}} tareas importadas', { count: result.tasks })
            : t('{{channels}} canales nuevos y {{messages}} mensajes', {
                channels: result.channels,
                messages: result.messages,
              }),
        tone: 'success',
      });
    },
    onError: (err) =>
      toast({ title: t('No se pudo importar'), description: errorMessage(err), tone: 'error' }),
    onSettled: () => setProgress(0),
  });

  return (
    <section className="mt-6 border-t border-border pt-4">
      <h3 className="text-sm font-semibold">{t('Importar desde otras herramientas')}</h3>
      <p className="mt-1 text-sm text-muted">
        {t('Trae tus conversaciones y tareas para no empezar de cero.')}
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        {sources.map((item) => (
          <Button
            key={item.id}
            variant="secondary"
            size="sm"
            icon={<Upload className="size-4" />}
            disabled={run.isPending}
            loading={run.isPending && source?.id === item.id}
            onClick={() => {
              setSource(item);
              // Let React set `accept` before opening the picker.
              setTimeout(() => input.current?.click());
            }}
          >
            {item.name}
          </Button>
        ))}
      </div>
      {run.isPending && progress > 0 && progress < 1 && (
        <Progress value={progress * 100} max={100} label={t('Subiendo…')} className="mt-3" />
      )}
      {source && <p className="mt-2 text-xs text-muted">{t(source.how)}</p>}
      <input
        ref={input}
        type="file"
        hidden
        accept={source?.accept}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file && source) run.mutate({ from: source.id, file });
        }}
      />
    </section>
  );
}
